import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Package, ChevronDown, Wrench, Zap, Paperclip } from "lucide-react";
import api from "./../services/shopify";

const COLLECTION_CACHE_TTL = 3 * 60 * 1000; // 3 minutes

let cachedCollections = null;
let collectionsCacheTime = 0;
let collectionsRequest = null;

const SparePartsDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hovered, setHovered] = useState("");

  const [collections, setCollections] = useState({
    attachments: null,
    service: null,
    starter: null,
  });

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

const fetchCollections = async () => {
  const now = Date.now();
  if (
    cachedCollections &&
    now - collectionsCacheTime < COLLECTION_CACHE_TTL
  ) {
    setCollections(cachedCollections);
    return;
  }

  try {
    if (!collectionsRequest) {
      const collectionsQuery = `
        {
          collections(first: 20) {
            edges {
              node {
                id
                title
                handle
                image {
                  url
                }
                products(first: 250) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      `;

      collectionsRequest = api.post("", {
        query: collectionsQuery,
      });
    }

    const { data } = await collectionsRequest;

    const shopifyCollections =
      data?.data?.collections?.edges?.map((item) => item.node) || [];

    const findCollection = (title) =>
      shopifyCollections.find(
        (collection) =>
          collection.title.trim().toLowerCase() ===
          title.trim().toLowerCase(),
      );

    const newCollections = {
      attachments: findCollection("SYM QWF35S Compatible Attachments"),
      service: findCollection("Service Kit"),
      starter: findCollection("Electric Start Kit"),
    };

    // ✅ save cache
    cachedCollections = newCollections;
    collectionsCacheTime = Date.now();

    setCollections(newCollections);
  } catch (err) {
    console.error("Error fetching Shopify collections:", err);
  } finally {
    collectionsRequest = null;
  }
};
  const goToSpareParts = () => {
    setIsOpen(false);

    if (location.pathname !== "/spareparts") {
      navigate("/spareparts");
    }
  };
  const goToCollection = (handle) => {
    setIsOpen(false);
    setHovered("");

    navigate(`/spareparts?collection=${handle}`);
  };

  const getProductCount = (collection) => {
    return collection?.products?.edges?.length || 0;
  };

  return (
    <div
      ref={dropdownRef}
      className="relative inline-block"
onMouseEnter={() => {
  setIsOpen(true);
  fetchCollections();
}}
      onMouseLeave={() => {
        setIsOpen(false);
        setHovered("");
      }}
    >
      <div className="flex items-center rounded-lg hover:text-yellow-500">
        <Link
          to="/spareparts"
          onClick={() => setIsOpen(false)}
          className="flex items-center gap-1.5 px-3 py-1.5"
        >
          <Package className="w-4 h-4" />
          Spare Parts
        </Link>

        <button onClick={goToSpareParts} className="px-2 py-1.5">
          <ChevronDown
            className={`w-4 h-4 transition ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full w-72 pt-2 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden">
            {/* Attachments */}
            <button
              onClick={() => goToCollection(collections.attachments?.handle)}
              onMouseEnter={() => setHovered("attachments")}
              className={`w-full flex items-center justify-between px-4 py-3 transition ${
                hovered === "attachments"
                  ? "bg-yellow-50 dark:bg-yellow-500/10"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center">
                  <Paperclip className="w-5 h-5 text-yellow-600" />
                </div>

                <div className="text-left">
                  <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                    Attachments
                  </h4>

                  <p className="text-xs text-slate-500">
                    SYM QWF35S compatible attachments
                  </p>
                </div>
              </div>

              <span className="min-w-7 h-7 px-2 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold flex items-center justify-center">
                {getProductCount(collections.attachments)}
              </span>
            </button>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Service Kit */}
            <button
              onClick={() => goToCollection(collections.service?.handle)}
              onMouseEnter={() => setHovered("service")}
              className={`w-full flex items-center justify-between px-4 py-3 transition ${
                hovered === "service"
                  ? "bg-yellow-50 dark:bg-yellow-500/10"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-yellow-600" />
                </div>

                <div className="text-left">
                  <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                    Service Kit
                  </h4>

                  <p className="text-xs text-slate-500">
                    Maintenance spare parts
                  </p>
                </div>
              </div>

              <span className="min-w-7 h-7 px-2 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold flex items-center justify-center">
                {getProductCount(collections.service)}
              </span>
            </button>

            <div className="border-t border-slate-200 dark:border-slate-700" />

            {/* Electric Starter Kit */}
            <button
              onClick={() => goToCollection(collections.starter?.handle)}
              onMouseEnter={() => setHovered("starter")}
              className={`w-full flex items-center justify-between px-4 py-3 transition ${
                hovered === "starter"
                  ? "bg-yellow-50 dark:bg-yellow-500/10"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-yellow-600" />
                </div>

                <div className="text-left">
                  <h4 className="text-sm font-semibold text-slate-800 dark:text-white">
                    Electric Starter Kit
                  </h4>

                  <p className="text-xs text-slate-500">
                    Starter conversion kits
                  </p>
                </div>
              </div>

              <span className="min-w-7 h-7 px-2 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-semibold flex items-center justify-center">
                {getProductCount(collections.starter)}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SparePartsDropdown;
